"use client";

import * as React from "react";
import { ChevronDown, ShieldCheck, Receipt, Clock, UserCheck, Download } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.14,
      delayChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
export default function LEssenzaDelGusto() {
  return <section data-section-id="223400" id="rapporto-risparmio-2026-impianti-in-albania" className="bg-background text-foreground relative min-h-screen sm:min-h-[720px] lg:min-h-[820px] w-full overflow-hidden flex items-center justify-center py-28 sm:py-36 lg:py-44">
      {/* Background image */}
      <div className="absolute inset-0 z-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663456/img/modern_dental_laboratory_albania_2026.png')] bg-fixed bg-cover bg-center" />

      {/* Editorial overlay gradient using palette tokens */}
      <div className="absolute inset-0 z-10 bg-background/50 bg-gradient-to-b from-background/30 via-background/70 to-background" />

      {/* Main hero content container */}
      <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
      once: true,
      amount: 0.2
    }} className="relative z-20 flex h-full w-full max-w-5xl flex-col items-center justify-center px-4 sm:px-6 lg:px-8 text-center">
        <div className="mb-8 flex flex-wrap items-center justify-center gap-3">
          <motion.div variants={itemVariants}>
            <Badge variant="outline" className="border-primary/25 bg-accent/70 text-accent-foreground backdrop-blur-md uppercase tracking-[0.25em] py-2 px-6 font-mono text-[10px] sm:text-xs shadow-xs">
              Guida 2026
            </Badge>
          </motion.div>
          <motion.div variants={itemVariants}>
            <Badge variant="outline" className="border-primary/25 bg-accent/70 text-accent-foreground backdrop-blur-md uppercase tracking-[0.25em] py-2 px-6 font-mono text-[10px] sm:text-xs shadow-xs">
              <ShieldCheck className="size-3.5 text-primary" />
              Guida per pazienti italiani
            </Badge>
          </motion.div>
        </div>

        <motion.div variants={itemVariants} className="relative flex items-center justify-center w-full max-w-6xl">
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-foreground mb-8 max-w-5xl leading-[1.08] font-medium tracking-tight">
            <span className="inline-block">
              Impianti Dentali in Albania 2026: Guida Completa per Pazienti Italiani
            </span>
          </h1>
        </motion.div>

        <motion.div variants={itemVariants} className="relative w-full max-w-3xl mb-12">
          <p className="font-default text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed">
            Prezzi per voce, sistema implantare dichiarato e un piano scritto entro 24 ore: cosa sapere prima di scegliere un impianto dentale a Tirana.
          </p>
        </motion.div>

        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full">
          <Button onClick={() => document.querySelector("#interactivecostcalculator")?.scrollIntoView({
          behavior: "smooth"
        })} className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-10 sm:px-14 py-7 sm:py-8 text-sm sm:text-base font-medium tracking-widest uppercase shadow-md h-auto transition-[color,background-color,border-color,box-shadow] duration-300 hover:shadow-lg cursor-pointer">
            Calcola il costo
          </Button>

          <Button asChild variant="outline" className="w-full sm:w-auto border-primary text-primary hover:bg-primary/10 hover:text-primary rounded-lg px-10 sm:px-14 py-7 sm:py-8 text-sm sm:text-base font-medium tracking-widest uppercase shadow-md h-auto transition-[color,background-color,border-color,box-shadow] duration-300 hover:shadow-lg">
            <Link to="https://wa.me/393892866221?text=Buongiorno%2C%20vorrei%20ricevere%20il%20piano%20scritto%20per%20un%20impianto%20dentale.%20%5BIDCO-IT-PLAN-SKY%5D" newTab>
              Scrivici su WhatsApp
            </Link>
          </Button>
        </motion.div>

        {/* PDF Download Link */}
        <motion.div variants={itemVariants} className="mt-6 flex justify-center">
          <Link to="https://impiantidentali.co/wp-content/uploads/2026/10/guida-impianti-dentali-albania-2026.pdf" newTab download className="text-sm text-muted-foreground underline underline-offset-4 hover:text-primary transition-colors inline-flex items-center gap-1.5">
            <Download className="size-4" />
            Scarica la guida del paziente (PDF)
          </Link>
        </motion.div>

        {/* Static frosted chips row (visible on all screen sizes) */}
        <motion.div variants={itemVariants} className="flex flex-wrap items-center justify-center gap-4 mt-8">
          <div className="flex items-center gap-2 bg-white border border-[#0f766e]/35 rounded-full px-4 py-2 text-sm font-semibold shadow-md" style={{
          color: '#0f1f3a'
        }}>
            <Receipt className="size-4 text-primary shrink-0" />
            <span>Listino 2026 per voce</span>
          </div>
          <div className="flex items-center gap-2 bg-white border border-[#0f766e]/35 rounded-full px-4 py-2 text-sm font-semibold shadow-md" style={{
          color: '#0f1f3a'
        }}>
            <Clock className="size-4 text-primary shrink-0" />
            <span>Piano scritto entro 24 ore</span>
          </div>
          <div className="flex items-center gap-2 bg-white border border-[#0f766e]/35 rounded-full px-4 py-2 text-sm font-semibold shadow-md" style={{
          color: '#0f1f3a'
        }}>
            <UserCheck className="size-4 text-primary shrink-0" />
            <span>Referente in italiano</span>
          </div>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 z-20 -translate-x-1/2 flex flex-col items-center gap-3 opacity-60 hover:opacity-100 transition-opacity">
        <span className="text-muted-foreground text-[10px] uppercase tracking-[0.4em] font-mono">
          Scopri di più
        </span>
        <ChevronDown className="text-primary size-6 animate-bounce" strokeWidth={1.5} />
      </div>
    </section>;
}